import React from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { useAccessibility } from '../contexts/AccessibilityContext';

const AccessibilityPage: React.FC = () => {
  const { t } = useLanguage();
  const { highContrast, textSizeMode, toggleHighContrast, setTextSizeMode } = useAccessibility();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Accessibility Features</h1>
          
          <div className="prose max-w-none">
            <p className="text-gray-600 mb-6">
              We are committed to ensuring our website is accessible to all users, including those with disabilities. 
              This page provides information about the accessibility features available on our site.
            </p>
            
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Available Accessibility Features</h2>
            
            <div className="space-y-6">
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Skip to Main Content</h3>
                <p className="text-gray-600 mb-3">
                  This feature allows users to bypass repetitive navigation links and jump directly to the main content of the page.
                </p>
                <p className="text-gray-600">
                  To use this feature, press the Tab key when the page loads to reveal the "Skip to Main Content" link, 
                  then press Enter to navigate directly to the main content area.
                </p>
              </div>
              
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">High Contrast Mode</h3>
                <p className="text-gray-600 mb-3">
                  High contrast mode improves readability by increasing the contrast between text and background elements.
                </p>
                <button
                  onClick={toggleHighContrast}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {highContrast ? 'Disable High Contrast Mode' : 'Enable High Contrast Mode'}
                </button>
              </div>
              
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Text Size Options</h3>
                <p className="text-gray-600 mb-3">
                  Adjust the text size to improve readability according to your preferences.
                </p>
                <div className="flex items-center space-x-4">
                  <span className="text-gray-700">Text Size:</span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setTextSizeMode('small')}
                      className={`px-3 py-1 rounded ${textSizeMode === 'small' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      Small
                    </button>
                    <button
                      onClick={() => setTextSizeMode('medium')}
                      className={`px-3 py-1 rounded ${textSizeMode === 'medium' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      Medium
                    </button>
                    <button
                      onClick={() => setTextSizeMode('large')}
                      className={`px-3 py-1 rounded ${textSizeMode === 'large' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      Large
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Screen Reader Compatibility</h3>
                <p className="text-gray-600">
                  Our website is designed to be compatible with screen readers. We use proper heading structures, 
                  alternative text for images, and semantic HTML to ensure content is accessible to assistive technologies.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Keyboard Navigation</h3>
                <p className="text-gray-600">
                  All interactive elements on our website can be accessed using keyboard navigation. 
                  Use the Tab key to move between interactive elements, and Enter or Space to activate them.
                </p>
              </div>
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Feedback and Assistance</h2>
            <p className="text-gray-600">
              If you experience any difficulties accessing our website or have suggestions for improving accessibility, 
              please contact our support team at{' '}
              <a href="mailto:support@civicreporter.gov.in" className="text-blue-600 hover:underline">
                support@civicreporter.gov.in
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessibilityPage;